import React, { useMemo, useState, useCallback, useContext } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  ThemeProvider,
  Typography,
  Button,
  Paper,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import HistoryIcon from '@mui/icons-material/History';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { MaterialReactTable } from 'material-react-table';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAPI } from '../Hooks/useAPIs';
import { useUrlFilters, createFieldConfig, createDateConfig } from '../Hooks/useUrlFilters';
import { useReusableTable } from '../common/TablePanel/tablePanel';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import { buildQueryString, sortingToOrdering } from '../common/TablePanel/tableUtils';
import { useUrlSorting } from '../Hooks/useUrlSorting';
import FiltersComponent from '../common/FiltersPanel/FIltersPanel';
import CreateDeviceDialog from '../common/Dialogs/Devices/devices';
import AddMultipleDevicesDialog from '../common/Dialogs/Devices/AddMultipleDevicesDialog';
import ApproveRegistrationDialog from '../common/Dialogs/Devices/ApproveRegistrationDialog';
import RegistrationRequestDialog from '../common/Dialogs/Devices/RegistrationRequestDialog';
import TransferRequestDialog from '../common/Dialogs/Devices/TransferRequestDialog';
import EditDeviceDialog from '../common/Dialogs/Devices/EditDeviceDialog';
import DeviceRegistrationHistoryDialog from '../common/Dialogs/Devices/DeviceRegistrationHistoryDialog';
import { sanitizeMacAddress } from '../utils/macAddress';
import DeviceNotesDialog from '../common/Dialogs/Devices/DeviceNotesDialog';
import { useDeviceColumns, useRegistrationColumns } from './components/DeviceColumns';
import { RegistrationCard } from './components/RegistrationCard';
import { DeviceFilters } from './components/DeviceFilters';
import WiviSyncNotice from './components/WiviSyncNotice';
import { normalizeDeviceType } from '../utils/deviceTypes';
import './DeviceView.css';

const filterConfig = {
  fields: {
    vin: createFieldConfig('vin', { type: 'array', defaultValue: [] }),
    mac_addr: createFieldConfig('mac_addr', { type: 'array', defaultValue: [] }),
    remote_fleet_name: createFieldConfig('remote_fleet_name', { type: 'array', defaultValue: [] }),
    region: createFieldConfig('region', { type: 'array', defaultValue: [] }),
    user: createFieldConfig('user', { type: 'array', defaultValue: [] }),
    device_type: createFieldConfig('device_type', { type: 'array', defaultValue: [] }),
    online: createFieldConfig('online', { type: 'array', defaultValue: [] }),
    running: createFieldConfig('running', { type: 'array', defaultValue: [] }),
    logging: createFieldConfig('logging', { type: 'array', defaultValue: [] }),
  },
  dateFields: [
    createDateConfig('start_datetime', 'end_datetime', {
      startParam: 'start_date',
      endParam: 'end_date',
      allowEmpty: true,
      defaultStart: null,
      defaultEnd: null,
    }),
  ],
  debounceDelay: 150,
};

const REQUEST_TYPE_OPTIONS = [
  { value: '', label: 'All Request Types' },
  { value: 'ownership_transfer', label: 'Ownership Transfer' },
  { value: 'new_device', label: 'New Device' },
  { value: 'registration_renewal', label: 'Registration Renewal' },
  { value: 'tailscale_approval', label: 'Tailscale Approval' },
  { value: 'device_lost', label: 'Device Lost' },
  { value: 'device_replacement', label: 'Device Replacement' },
  { value: 'device_return', label: 'Device Return' },
];

const DEVICE_FILTER_MAPPING = {
  vin: 'vin',
  mac_addr: 'mac_addr',
  remote_fleet_name: 'remote_fleet_name',
  region: 'region',
  user: 'user',
  device_type: 'device_type',
  online: 'inventory_online',
  running: 'inventory_running',
  logging: 'inventory_logging',
};

const AdminDeviceView = ({ activeTheme }) => {
  const [viewMode, setViewMode] = useState('devices');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 });
  const [sorting, setSorting] = useUrlSorting([{ id: 'last_activity', desc: true }]);
  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ mac_addr: '', region_code: '', user_id: '', device_type: '' });
  const [createError, setCreateError] = useState('');
  const [addMultipleDevicesDialogOpen, setAddMultipleDevicesDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [editDeviceDialogOpen, setEditDeviceDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [registrationType, setRegistrationType] = useState(null);
  const [pendingRequestTypeFilter, setPendingRequestTypeFilter] = useState('');
  
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuDevice, setMenuDevice] = useState(null);

  const { apiCall, isMsalReady } = useAPI();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { filters, debouncedFilters, handleFilterChange, handleDateRangeChange } = useUrlFilters(filterConfig);
  const { formatDateTime, createIsoStringWithTimezone, userTimezone, handleTimezoneChange } = useContext(TimezoneContext);

  const filterOptionsQuery = useQuery({
    queryKey: ['device-filters', debouncedFilters],
    queryFn: async () => {
      const params = buildQueryString(debouncedFilters, DEVICE_FILTER_MAPPING, createIsoStringWithTimezone);
      const response = await apiCall('GET', `api/v1/devices/filters?${params.toString()}`);
      return response.data.data;
    },
    enabled: false,
    placeholderData: keepPreviousData,
  });

  const deviceQuery = useQuery({
    queryKey: ['device-data', debouncedFilters, pagination.pageIndex, pagination.pageSize, sorting],
    queryFn: async () => {
      const params = buildQueryString(debouncedFilters, DEVICE_FILTER_MAPPING, createIsoStringWithTimezone);
      params.append('page', (pagination.pageIndex + 1).toString());
      params.append('page_size', pagination.pageSize.toString());
      const ordering = sortingToOrdering(sorting);
      if (ordering) params.append('ordering', ordering);
      const response = await apiCall('GET', `api/v1/devices/query?${params.toString()}`);
      return response.data;
    },
    enabled: isMsalReady && viewMode === 'devices',
    placeholderData: keepPreviousData,
  });

  const pendingRegistrationsQuery = useQuery({
    queryKey: ['pending-requests', pagination.pageIndex, pagination.pageSize, pendingRequestTypeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('status', 'pending');
      params.append('page', (pagination.pageIndex + 1).toString());
      params.append('page_size', pagination.pageSize.toString());
      if (pendingRequestTypeFilter) {
        params.append('request_type', pendingRequestTypeFilter);
      }
      const response = await apiCall('GET', `api/v1/devices/requests?${params.toString()}`);
      return response.data;
    },
    enabled: isMsalReady && viewMode === 'pending',
    placeholderData: keepPreviousData,
    refetchInterval: 30000,
  });

  const regionsQuery = useQuery({
    queryKey: ['regions'],
    queryFn: async () => {
      const resp = await apiCall('GET', '/api/regions');
      return resp.data;
    },
    enabled: isMsalReady,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const data = useMemo(() => {
    if (viewMode === 'pending') {
      return pendingRegistrationsQuery.data?.results || [];
    }
    return deviceQuery.data?.results || [];
  }, [viewMode, deviceQuery.data, pendingRegistrationsQuery.data]);

  const REGIONS = regionsQuery.data || [];
  const isRegionsLoading = regionsQuery.isLoading;

  const availableRegions = useMemo(() => {
    return REGIONS;
  }, [REGIONS]);

  const updateDeviceMutation = useMutation({
    mutationFn: async ({ id, updates }) => {
      const response = await apiCall('PATCH', `api/v1/devices/${id}/`, updates);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-data'] });
      queryClient.invalidateQueries({ queryKey: ['device-filters'] });
    },
  });

  const createDeviceMutation = useMutation({
    mutationFn: async (newDevice) => {
      const response = await apiCall('POST', 'api/v1/devices/create', newDevice);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-data'] });
      queryClient.invalidateQueries({ queryKey: ['device-filters'] });
      setOpenCreate(false);
      setCreateForm({ mac_addr: '', region_code: '', user_id: '', device_type: '' });
      setCreateError('');
    },
    onError: (error) => {
      setCreateError(error.response?.data?.detail || error.message || 'Failed to create device');
    },
  });

  const createMultipleDevicesMutation = useMutation({
    mutationFn: async (requestData) => {
      const sanitizedRequestData = {
        ...requestData,
        devices: (requestData.devices || []).map((device) => ({
          ...device,
          mac_addr: sanitizeMacAddress(device.mac_addr),
        })),
      };
      const response = await apiCall('POST', 'api/v1/devices/create-multiple', sanitizedRequestData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-data'] });
      queryClient.invalidateQueries({ queryKey: ['device-filters'] });
      setAddMultipleDevicesDialogOpen(false);
    },
    onError: (error) => {
      console.error('Failed to create multiple devices:', error);
    },
  });

  const createDeviceRequestMutation = useMutation({
    mutationFn: async (requestData) => {
      const response = await apiCall('POST', 'api/v1/devices/requests', requestData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-requests'] });
      queryClient.invalidateQueries({ queryKey: ['pending-requests'] });
      queryClient.invalidateQueries({ queryKey: ['device-data'] });
      setRegistrationDialogOpen(false);
      setSelectedDevice(null);
    },
  });

  const approveRequestMutation = useMutation({
    mutationFn: async ({ requestId, notes, expiry_date }) => {
      const response = await apiCall('POST', `api/v1/devices/requests/${requestId}/approve`, { 
        notes,
        ...(expiry_date && { expiry_date }),
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-requests'] });
      queryClient.invalidateQueries({ queryKey: ['device-data'] });
      setApprovalDialogOpen(false);
      setSelectedRegistration(null);
    },
  });

  const denyRequestMutation = useMutation({
    mutationFn: async ({ requestId, rejection_reason, notes }) => {
      const response = await apiCall('POST', `api/v1/devices/requests/${requestId}/deny`, { 
        rejection_reason: rejection_reason || notes,
        notes 
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-requests'] });
      setApprovalDialogOpen(false);
      setSelectedRegistration(null);
    },
  });

  const handleCreate = () => {
    setCreateError('');
    const mac = sanitizeMacAddress(createForm.mac_addr);

    if (!/^[A-Za-z0-9]{12,}$/.test(mac)) {
      setCreateError('MAC address must be at least 12 alphanumeric characters.');
      return;
    }

    const deviceType = normalizeDeviceType(createForm.device_type) || null;

    createDeviceMutation.mutate(
      {
        mac_addr: mac,
        region: createForm.region_code
          ? REGIONS.find(r => r.code === createForm.region_code)?.id
          : null,
        user: createForm.user_id || null,
        device_type: deviceType,
      },
      {
        onError: (error) => {
          const data = error?.response?.data;
          if (data?.mac_addr) {
            setCreateError(data.mac_addr[0]);
          } else if (data?.region) {
            setCreateError(data.region[0]);
          } else {
            setCreateError(data?.detail || 'Failed to create device');
          }
        },
      }
    );
  };

  const handleSaveDevice = useCallback(
    ({ values, row, table }) => {
      const original = row.original;
      const updates = {};

      if (values.is_active !== original.is_active) {
        updates.is_active = values.is_active;
      }

      const newRegionCode =
        values.region_code !== undefined ? values.region_code : original.region?.code || '';
      const oldRegionCode = original.region?.code || '';
      if (newRegionCode !== oldRegionCode) {
        updates.region = newRegionCode
          ? REGIONS.find(r => r.code === newRegionCode)?.id ?? null
          : null;
      }

      const newUserObj = values.user_obj !== undefined ? values.user_obj : original.user;
      if (newUserObj?.id !== original.user?.id) {
        updates.user = newUserObj?.id ?? null;
      }

      const newTailscaleStatus = values.tailscale_approval_status !== undefined 
        ? values.tailscale_approval_status 
        : original.tailscale_approval_status || null;
      const oldTailscaleStatus = original.tailscale_approval_status || null;
      if (newTailscaleStatus !== oldTailscaleStatus) {
        updates.tailscale_approval_status = newTailscaleStatus;
      }

      const newPi = values.pi !== undefined ? values.pi : (row._valuesCache?.pi !== undefined ? row._valuesCache.pi : original.pi);
      if (newPi !== original.pi) {
        updates.pi = newPi || null;
      }

      const newDeviceType = values.device_type !== undefined ? values.device_type : (row._valuesCache?.device_type !== undefined ? row._valuesCache.device_type : original.device_type);
      if (newDeviceType !== original.device_type) {
        updates.device_type = newDeviceType || null;
      }

      const newIp = values.ip !== undefined ? values.ip : (row._valuesCache?.ip !== undefined ? row._valuesCache.ip : original.ip);
      if (newIp !== original.ip) {
        updates.ip = newIp || null;
      }

      const newIsLost = values.is_lost !== undefined ? values.is_lost : (row._valuesCache?.is_lost !== undefined ? row._valuesCache.is_lost : original.is_lost);
      if (newIsLost !== original.is_lost) {
        updates.is_lost = newIsLost;
      }

      const newIsBroken = values.is_broken !== undefined ? values.is_broken : (row._valuesCache?.is_broken !== undefined ? row._valuesCache.is_broken : original.is_broken);
      if (newIsBroken !== original.is_broken) {
        updates.is_broken = newIsBroken;
      }

      const newRegStatus = values.device_registration_status !== undefined 
        ? values.device_registration_status 
        : (row._valuesCache?.device_registration_status !== undefined 
          ? row._valuesCache.device_registration_status 
          : (original.device_registration_status || null));
      const oldRegStatus = original.device_registration_status || null;
      if (newRegStatus !== oldRegStatus) {
        updates.device_registration_status = newRegStatus;
      }

      if (Object.keys(updates).length === 0) {
        table.setEditingRow(null);
        return;
      }

      updateDeviceMutation.mutate(
        { id: original.id, updates },
        { onSuccess: () => table.setEditingRow(null) }
      );
    },
    [updateDeviceMutation, REGIONS]
  );

  const handleMenuOpen = (event, device) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuDevice(device);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuDevice(null);
  };

  const handleRequestTailscale = (device) => {
    if (device?.is_lost || device?.is_broken) {
      return;
    }
    setSelectedDevice(device);
    setRegistrationType('tailscale_approval');
    setRegistrationDialogOpen(true);
    handleMenuClose();
  };

  const handleRequestRenewal = (device) => {
    if (device?.is_lost || device?.is_broken) {
      return;
    }
    setSelectedDevice(device);
    setRegistrationType('registration_renewal');
    setRegistrationDialogOpen(true);
    handleMenuClose();
  };

  const handleViewHistory = (device) => {
    setSelectedDevice(device);
    setHistoryDialogOpen(true);
    handleMenuClose();
  };

  const handleViewNotes = (device) => {
    setSelectedDevice(device);
    setNotesDialogOpen(true);
    handleMenuClose();
  };

  const handleEditDevice = (device) => {
    setSelectedDevice(device);
    setEditDeviceDialogOpen(true);
    handleMenuClose();
  };

  const handleSaveDeviceOverwrite = (updates) => {
    if (selectedDevice) {
      updateDeviceMutation.mutate(
        { id: selectedDevice.id, updates },
        {
          onSuccess: () => {
            setEditDeviceDialogOpen(false);
            setSelectedDevice(null);
          },
        }
      );
    }
  };

  const handleReportLost = (device) => {
    if (device?.is_lost || device?.is_broken) {
      return;
    }
    setSelectedDevice(device);
    setRegistrationType('device_lost');
    setRegistrationDialogOpen(true);
    handleMenuClose();
  };

  const handleRequestReplacement = (device) => {
    if (device?.is_lost || device?.is_broken) {
      return;
    }
    setSelectedDevice(device);
    setRegistrationType('device_replacement');
    setRegistrationDialogOpen(true);
    handleMenuClose();
  };

  const handleRequestTransfer = (device) => {
    if (device?.is_lost || device?.is_broken) {
      return;
    }
    setSelectedDevice(device);
    setTransferDialogOpen(true);
    handleMenuClose();
  };

  const handleRegistrationSubmit = (requestData) => {
    if (selectedDevice) {
      createDeviceRequestMutation.mutate({
        request_type: requestData.registration_type || registrationType,
        device_id: selectedDevice.id,
        reason: requestData.reason || requestData.notes || `${registrationType} request`,
        notes: requestData.notes,
        ...(requestData.pi && { requested_pi: requestData.pi }),
        ...(requestData.device_type && { requested_device_type: requestData.device_type }),
        ...(requestData.expiry_date && { expiry_date: requestData.expiry_date }),
      });
    }
  };

  const handleTransferSubmit = (requestData) => {
    if (selectedDevice) {
      createDeviceRequestMutation.mutate({
        request_type: 'ownership_transfer',
        device_id: selectedDevice.id,
        requested_to: requestData.transfer_to_user_id,
        reason: requestData.notes || 'Transfer request',
        notes: requestData.notes,
      });
    }
  };

  const handleApproveRegistration = (registration) => {
    setSelectedRegistration(registration);
    setApprovalDialogOpen(true);
  };

  const handleDenyRegistration = (registration) => {
    setSelectedRegistration(registration);
    setApprovalDialogOpen(true);
  };

  const deviceColumns = useDeviceColumns({
    formatDateTime,
    availableRegions,
    isRegionsLoading,
    filterOptionsQuery: { isFetching: false },
  });

  const registrationColumns = useRegistrationColumns({ formatDateTime });

  const columns = viewMode === 'devices' ? deviceColumns : registrationColumns;
  const rowCount = viewMode === 'devices' 
    ? (deviceQuery.data?.count || 0)
    : (pendingRegistrationsQuery.data?.count || 0);
  const isLoading = viewMode === 'devices'
    ? deviceQuery.isFetching
    : pendingRegistrationsQuery.isFetching;

  const dateRangePicker = useMemo(() => (
    viewMode === 'devices' ? { filters: filters || {}, handleDateRangeChange } : undefined
  ), [viewMode, filters, handleDateRangeChange]);

  const viewModeSelector = useMemo(() => ({
    viewMode,
    handleViewModeChange: (newMode) => {
      if (newMode !== null) {
        setViewMode(newMode);
        setPagination({ pageIndex: 0, pageSize: 100 });
        if (newMode === 'pending') {
          setPendingRequestTypeFilter('');
        }
      }
    },
  }), [viewMode]);

  const requestTypeFilter = useMemo(() => ({
    value: pendingRequestTypeFilter,
    onChange: (value) => {
      setPendingRequestTypeFilter(value);
      setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    },
    options: REQUEST_TYPE_OPTIONS,
  }), [pendingRequestTypeFilter, pagination.pageSize]);

  const dropdowns = useMemo(() => (
    viewMode === 'devices' ? (
      <DeviceFilters
        filters={filters}
        debouncedFilters={debouncedFilters}
        handleFilterChange={handleFilterChange}
        createIsoStringWithTimezone={createIsoStringWithTimezone}
        mapping={DEVICE_FILTER_MAPPING}
        REGIONS={REGIONS}
      />
    ) : undefined
  ), [viewMode, filters, debouncedFilters, handleFilterChange, createIsoStringWithTimezone, REGIONS]);

  const handleOpenDetails = useCallback((device) => {
    navigate(`/devices/${device.id}`, {
      state: {
        device,
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  }, [navigate, location.pathname, location.search]);

  const { table } = useReusableTable({
    columns,
    data,
    rowCount,
    isLoading,
    enablePagination: true,
    paginationDisplayMode: 'pages',
    positionPagination: 'bottom',
    enableBottomToolbar: true,
    enableStickyFooter: true,
    enableRowSelection: false,
    enableRowExpansion: false,
    enableCompareFiles: false,
    enableDefaultAddToCart: false,
    enableRowVirtualization: true,
    enableEditing: false,
    getRowId: (row) => row.id,
    pagination,
    sorting,
    onSortingChange: setSorting,
    manualPagination: true,
    onPaginationChange: setPagination,
    muiPaginationProps: {
      color: 'primary',
      rowsPerPageOptions: [100, 200, 500, 1000],
      showFirstButton: true,
      showLastButton: true,
    },
    enableCSVExport: true,
    csvExportFilename: 'devices-export.csv',
    muiBottomToolbarProps: { className: 'device-table-pagination' },
    muiTableContainerProps: {
      className: 'device-table-container',
      sx: {
        maxHeight: 'calc(100vh - 320px)',
        height: '100%',
        overflow: 'auto',
      },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Actions',
        size: 180,
        minSize: 180,
      },
    },
    renderTopToolbarCustomActions: () => (
      <Box className="device-toolbar-actions">
        {viewMode === 'devices' && (
          <>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreate(true)}
              size="small"
            >
              Add Device
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setAddMultipleDevicesDialogOpen(true)}
              size="small"
            >
              Add Multiple Devices
            </Button>
          </>
        )}
      </Box>
    ),
    renderRowActions: ({ row, table }) => {
      if (viewMode === 'devices') {
        const device = row.original;
        
        return (
          <Box className="device-row-actions">
            <Tooltip title="Open Details">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleOpenDetails(device)}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Overwrite">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEditDevice(device)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View History">
              <IconButton
                size="small"
                color="default"
                onClick={() => handleViewHistory(device)}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Notes">
              <IconButton
                size="small"
                color="default"
                onClick={() => handleViewNotes(device)}
              >
                <NoteAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="More Actions">
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, device)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      } else {
        return (
          <Box className="device-row-actions">
            <Tooltip title="Approve">
              <IconButton
                size="small"
                color="success"
                onClick={() => handleApproveRegistration(row.original)}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Deny">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDenyRegistration(row.original)}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    },
  });

  const renderPendingRegistrations = () => {
    if (viewMode !== 'pending') return null;

    const pendingResults = pendingRegistrationsQuery.data?.results || [];

    return (
      <Paper
        elevation={2}
        className="pending-requests-panel"
      >
        <Box className="pending-requests-header">
          <PendingActionsIcon className="pending-requests-header-icon" />
          <Typography variant="h6" className="pending-requests-header-title">
            Pending Requests
          </Typography>
        </Box>
        
        <Divider className="pending-requests-divider" />
        
        {pendingResults.length === 0 ? (
          <Box className="pending-requests-empty">
            <PendingActionsIcon className="pending-requests-empty-icon" />
            <Typography variant="h6" color="text.secondary" className="pending-requests-empty-title">
              No Pending Requests
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pendingRequestTypeFilter 
                ? `No pending ${REQUEST_TYPE_OPTIONS.find(o => o.value === pendingRequestTypeFilter)?.label || 'requests'} found.`
                : 'There are currently no pending device requests.'
              }
            </Typography>
          </Box>
        ) : (
          <>
            <Stack spacing={2}>
              {pendingResults.map((registration) => (
                <RegistrationCard
                  key={registration.id}
                  registration={registration}
                  formatDateTime={formatDateTime}
                  onDetails={handleApproveRegistration}
                />
              ))}
            </Stack>
            {pendingRegistrationsQuery.data?.count > pendingResults.length && (
              <Box className="pending-requests-footer">
                <Typography variant="body2" color="text.secondary">
                  Showing {pendingResults.length} of {pendingRegistrationsQuery.data?.count || 0} requests.
                  Use pagination controls below to view more.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    );
  };

  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="device-main-container">
        <Box className="device-sidebar">
          <FiltersComponent
            activeTheme={activeTheme}
            dateRangePicker={dateRangePicker}
            timezoneSelector={{ timezone: userTimezone, userTimezone, handleTimezoneChange }}
            viewModeSelector={viewModeSelector}
            requestTypeFilter={requestTypeFilter}
            dropdowns={dropdowns}
          />
        </Box>

        <Box className="device-content">
          {renderPendingRegistrations()}
          {viewMode === 'devices' && (
            <>
              <WiviSyncNotice />
              <MaterialReactTable table={table} />
            </>
          )}
        </Box>
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem 
          onClick={() => menuDevice && handleRequestRenewal(menuDevice)}
          disabled={menuDevice?.is_lost || menuDevice?.is_broken}
        >
          <ListItemIcon>
            <AutorenewIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Renew Registration</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => menuDevice && handleRequestTailscale(menuDevice)}
          disabled={menuDevice?.is_lost || menuDevice?.is_broken}
        >
          <ListItemIcon>
            <VerifiedUserIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText>Renew Tailscale</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => menuDevice && handleRequestTransfer(menuDevice)}
          disabled={menuDevice?.is_lost || menuDevice?.is_broken}
        >
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Transfer Ownership</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => menuDevice && handleRequestReplacement(menuDevice)}
          disabled={menuDevice?.is_lost || menuDevice?.is_broken}
        >
          <ListItemIcon>
            <FindReplaceIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText>Request Replacement</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => menuDevice && handleReportLost(menuDevice)}
          disabled={menuDevice?.is_lost || menuDevice?.is_broken}
        >
          <ListItemIcon>
            <ReportProblemIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Report Lost</ListItemText>
        </MenuItem>
        {(menuDevice?.is_lost || menuDevice?.is_broken) && (
          <>
            <Divider />
            <MenuItem disabled>
              <ListItemText 
                primary={menuDevice?.is_lost ? "Device is lost - requests disabled" : "Device is broken - requests disabled"}
                primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', className: 'disabled-device-note' }}
              />
            </MenuItem>
          </>
        )}
      </Menu>

      <CreateDeviceDialog
        open={openCreate}
        onClose={() => {
          setOpenCreate(false);
          setCreateForm({ mac_addr: '', region_code: '', user_id: '', device_type: '' });
          setCreateError('');
        }}
        createForm={createForm}
        setCreateForm={setCreateForm}
        createError={createError}
        createDeviceMutation={createDeviceMutation}
        handleCreate={handleCreate}
        REGIONS={REGIONS}
      />

      <AddMultipleDevicesDialog
        open={addMultipleDevicesDialogOpen}
        onClose={() => {
          setAddMultipleDevicesDialogOpen(false);
        }}
        onSubmit={(requestData) => {
          createMultipleDevicesMutation.mutate(requestData);
        }}
        isLoading={createMultipleDevicesMutation.isLoading}
        enableUserAssignment={true}
        enableRegionSelection={true}
        REGIONS={REGIONS}
      />

      <ApproveRegistrationDialog
        open={approvalDialogOpen}
        onClose={() => {
          setApprovalDialogOpen(false);
          setSelectedRegistration(null);
        }}
        registration={selectedRegistration}
        onApprove={(notes) => {
          if (selectedRegistration) {
            approveRequestMutation.mutate({
              requestId: selectedRegistration.id,
              notes,
            });
          }
        }}
        onDeny={(notes) => {
          if (selectedRegistration) {
            denyRequestMutation.mutate({
              requestId: selectedRegistration.id,
              notes,
            });
          }
        }}
        isLoading={approveRequestMutation.isLoading || denyRequestMutation.isLoading}
      />

      <RegistrationRequestDialog
        open={registrationDialogOpen}
        onClose={() => {
          setRegistrationDialogOpen(false);
          setSelectedDevice(null);
          setRegistrationType(null);
        }}
        device={selectedDevice}
        registrationType={registrationType}
        onSubmit={handleRegistrationSubmit}
        isLoading={createDeviceRequestMutation.isLoading}
        isAdmin={true}
      />

      <TransferRequestDialog
        open={transferDialogOpen}
        onClose={() => {
          setTransferDialogOpen(false);
          setSelectedDevice(null);
        }}
        device={selectedDevice}
        onSubmit={handleTransferSubmit}
        isLoading={createDeviceRequestMutation.isLoading}
      />

      <EditDeviceDialog
        open={editDeviceDialogOpen}
        onClose={() => {
          setEditDeviceDialogOpen(false);
          setSelectedDevice(null);
        }}
        device={selectedDevice}
        availableRegions={availableRegions}
        isRegionsLoading={isRegionsLoading}
        onSubmit={handleSaveDeviceOverwrite}
        isLoading={updateDeviceMutation.isLoading}
      />

      <DeviceRegistrationHistoryDialog
        open={historyDialogOpen}
        onClose={() => {
          setHistoryDialogOpen(false);
          setSelectedDevice(null);
        }}
        device={selectedDevice}
      />

      <DeviceNotesDialog
        open={notesDialogOpen}
        onClose={() => {
          setNotesDialogOpen(false);
          setSelectedDevice(null);
        }}
        device={selectedDevice}
      />
    </ThemeProvider>
  );
};

export default AdminDeviceView;
